import type { ConsignmentInboundItem, ConsignmentOutboundItem } from '@cuito/types';
import type { ConsignmentTab } from '../../api/consignments';
import ConsignmentEntriesTable from './ConsignmentEntriesTable';

/**
 * 寄售台账表（UI 规范 550 行：对方社团 / 地点 / 商品 / 发出 / 已售 / 剩余 / 佣金率 / 已结算 / **应收（应付）余额**）。
 *
 * ## 为什么有一个 `consignmentRowOf` 适配器
 *
 * 两个视角**同形但不同名**：借出给 `expectedAmount` + `holderTenant` + `product.price`；
 * 受托给 `payableAmount` + `ownerTenant` + 无 `price`（§14.2）。把差异收在一个**纯函数**里，
 * 表格就只认一种行形状 —— 差异散进 JSX 的话，改一个视角必然漏掉另一个。
 * **适配器只做改名与取值，不做任何算术**（余额是服务端算好的）。
 * 行形状与 `tab` 不一致时**抛**（把「端点接反」从 `undefined.id` 的 TypeError 变成一句话）。
 *
 * ## `data-*-id` 而不是新 testid
 *
 * 冻结文档 §五 的 testid 清单是**闭集**：需要额外定位点时一律用属性
 * （`data-consignment-id` / `data-direction`），绝不新增 testid。
 */
export interface ConsignmentRow {
  id: string;
  /** 对方社团的 **id**（下拉的 `value` 必须是 id：契约的筛选参数是 uuid，不是名字） */
  counterpartyTenantId: string;
  counterpartyName: string;
  locationName: string;
  productName: string;
  quantitySent: number;
  quantitySold: number;
  quantityRemaining: number;
  /** 四位小数字符串（`"0.1000"`） */
  commissionRate: string;
  settledAmount: string;
  /** 借出 = 应收余额（`expectedAmount`）；受托 = 应付余额（`payableAmount`）。**同一个数**（§14.2） */
  balance: string;
  transferId: string | null;
}

export function consignmentRowOf(
  item: ConsignmentOutboundItem | ConsignmentInboundItem,
  tab: ConsignmentTab,
): ConsignmentRow {
  const common = {
    id: item.id,
    locationName: item.location.name,
    productName: item.product.name,
    quantitySent: item.quantitySent,
    quantitySold: item.quantitySold,
    quantityRemaining: item.quantityRemaining,
    commissionRate: item.commissionRate,
    settledAmount: item.settledAmount,
    transferId: item.transferId,
  };
  // **形状**（`holderTenant` vs `ownerTenant`）是运行时判据；`tab` 是调用方声明的视角。
  // 两者不一致 = 两个端点的响应接反了：静默走另一侧会读到 `undefined.id`，
  // 报出来的 TypeError 与真实原因（接错端点）毫无关系。这里显式挡掉。
  const shapedAsOutbound = 'holderTenant' in item;
  if (shapedAsOutbound !== (tab === 'outbound')) {
    throw new Error(
      `consignmentRowOf: ${tab} tab received a ${shapedAsOutbound ? 'outbound' : 'inbound'} row`,
    );
  }
  if ('holderTenant' in item) {
    return {
      ...common,
      counterpartyTenantId: item.holderTenant.id,
      counterpartyName: item.holderTenant.name,
      balance: item.expectedAmount,
    };
  }
  return {
    ...common,
    counterpartyTenantId: item.ownerTenant.id,
    counterpartyName: item.ownerTenant.name,
    balance: item.payableAmount,
  };
}

interface Props {
  tab: ConsignmentTab;
  rows: ConsignmentRow[];
  expandedId: string | null;
  detail: { entries: Parameters<typeof ConsignmentEntriesTable>[0]['entries'] } | null;
  detailLoading: boolean;
  detailError: string | null;
  onToggle: (id: string) => void;
}

export default function ConsignmentTable({
  tab,
  rows,
  expandedId,
  detail,
  detailLoading,
  detailError,
  onToggle,
}: Props) {
  const balanceLabel = tab === 'outbound' ? '应收余额' : '应付余额';
  const counterpartyLabel = tab === 'outbound' ? '受托方' : '货主';

  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="border-b-2 border-black text-left">
          <th className="py-2">{counterpartyLabel}</th>
          <th className="py-2">地点</th>
          <th className="py-2">商品</th>
          <th className="py-2">发出</th>
          <th className="py-2">已售</th>
          <th className="py-2">剩余</th>
          <th className="py-2">佣金率</th>
          <th className="py-2">已结算</th>
          <th className="py-2">{balanceLabel}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const expanded = expandedId === row.id;
          return [
            <tr
              key={row.id}
              data-testid="consignment-row"
              data-consignment-id={row.id}
              aria-expanded={expanded}
              onClick={() => onToggle(row.id)}
              className="cursor-pointer border-b border-gray-200 hover:bg-gray-50"
            >
              <td className="py-2 font-bold">{row.counterpartyName}</td>
              <td className="py-2">{row.locationName}</td>
              <td className="py-2">{row.productName}</td>
              <td className="py-2">{row.quantitySent}</td>
              <td className="py-2">{row.quantitySold}</td>
              <td className="py-2" data-testid="consignment-remaining">
                {row.quantityRemaining}
              </td>
              <td className="py-2">{row.commissionRate}</td>
              <td className="py-2" data-testid="consignment-settled">
                {row.settledAmount}
              </td>
              <td className="py-2 font-bold" data-testid="consignment-expected">
                {row.balance}
              </td>
            </tr>,
            expanded ? (
              <tr key={`${row.id}-entries`} className="border-b border-gray-200 bg-gray-50">
                <td colSpan={9} className="px-2 py-2">
                  <ConsignmentEntriesTable
                    entries={detail?.entries ?? []}
                    loading={detailLoading}
                    error={detailError}
                  />
                </td>
              </tr>
            ) : null,
          ];
        })}
      </tbody>
    </table>
  );
}
